import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { AxiosError } from "axios";
import { useAxiosPrivate } from "hooks/api/useAxiosPrivate";

import { Layout } from "components/Layout.tsx/Layout";
import { ConfigurationCard } from "components/ConfigurationCard/ConfigurationCard";
import { Footer } from "components/Footer/Footer";
import Loader from "components/Loader/Loader";
import { Modal } from "components/Modal/Modal";
import ConfirmationModalContent from "components/ConfirmationModalContent/ConfirmationModalContent";
import SuccessModalContent from "components/SuccessModalContent/SuccessModalContent";

import { CardItem } from "types/types";

import {
  SectionContainer,
  SectionHeader,
  SectionTitle,
  Container,
  EmptyConfiguration,
} from "./styles";

const cardItemsTypes: CardItem["type"][] = ["post", "article", "pin"];

export const ConfigurationList: React.FC = () => {
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [itemToDeleteId, setItemToDeleteId] = useState("");
  const [successModalOpen, setSuccessModalOpen] = useState(false);

  const navigate = useNavigate();

  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();

  const {
    data: configurationsList,
    isLoading,
    isError,
    error,
  } = useQuery<CardItem[], AxiosError>(
    "configurations-list",
    async () => {
      const response = await axiosPrivate.get("/configuration/list");
      return response.data;
    },
    {
      staleTime: 0,
      refetchOnMount: true,
    }
  );

  const deleteMutation = useMutation(
    () => axiosPrivate.delete(`/configuration/${itemToDeleteId}`),
    {
      onSuccess: (data) => {
        setItemToDeleteId("");
        setConfirmModalOpen(false);
        queryClient.invalidateQueries("configurations-list");
      },
      onError: (error) => {
        console.error("Error deleting item:", error);
      },
    }
  );

  const handleEdit = (id: string) => {
    navigate(`/configuration/edit/${id}`);
  };

  const handleDelete = (id: string) => {
    setItemToDeleteId(id);
    setConfirmModalOpen(true);
  };

  if (isLoading || deleteMutation.isLoading) {
    return <Loader />;
  }

  // TODO: Handle error state
  function setErrorModalOpen(arg0: boolean): void {
    throw new Error("Function not implemented.");
  }

  return (
    <Layout>
      {cardItemsTypes.map((itemsTypes, index) => {
        const filteredItems = configurationsList?.filter(
          (item) => item.type === itemsTypes
        );
        if (filteredItems?.length === 0) return null;
        return (
          <SectionContainer key={itemsTypes}>
            <SectionHeader>
              <SectionTitle>{itemsTypes}s Configurations:</SectionTitle>
            </SectionHeader>
            <Container>
              {filteredItems?.map((item) => (
                <ConfigurationCard
                  key={item.id}
                  item={item}
                  onEdit={handleEdit}
                  onDelete={() => handleDelete(item.id)}
                />
              ))}
              {filteredItems && filteredItems?.length == 0 && (
                <EmptyConfiguration>
                  - You have no {itemsTypes}s configuration yet !
                </EmptyConfiguration>
              )}
            </Container>
          </SectionContainer>
        );
      })}

      {confirmModalOpen && (
        <Modal
          onClose={() => {
            setItemToDeleteId("");
            setConfirmModalOpen(false);
          }}
        >
          <ConfirmationModalContent
            message="Are you sure you want to delete this item?"
            onCancel={() => {
              setItemToDeleteId("");
              setConfirmModalOpen(false);
            }}
            onConfirm={() => deleteMutation.mutate()}
            confirmLabel="Delete"
            variant="delete"
          />
        </Modal>
      )}
      {successModalOpen && (
        <Modal onClose={() => setSuccessModalOpen(false)}>
          <SuccessModalContent
            successMessage={"Configuration deleted successfully!"}
            onClose={() => setSuccessModalOpen(false)}
          />
        </Modal>
      )}
      <Footer />
    </Layout>
  );
};
